import type { AccountApi } from './types';
export function useAccountApi(): AccountApi {
  const { ensureAuthenticated } = useDjustAuth();
  const userStore = useUserStore();
  const { setAccountCookies, setStoreCookies, setStoreViewCookies, setCurrencyCookies } =
    useDjustAuth();
  /**
   * Retrieve a customer account with optional parameters.
   */
  const getCustomerAccount = async (
    config: Partial<DjustConfig> = {},
    useStore: boolean = true
  ): Promise<GetAccountResponse | null> => {
    await ensureAuthenticated();

    try {
      // Use Nuxt's useFetch composable to make the request with query parameters
      const { data, error } = await useFetch<GetAccountResponse>('/api/account', {
        query: config,
      });

      if (useStore && data) {
        userStore.setAccount(data.value.account);
      }

      // Handle any potential errors
      if (error.value) {
        console.error('Failed to retrieve customer account:', error.value);
        return null;
      }

      // Return the fetched customer account data
      return data.value;
    } catch (error) {
      // Handle unexpected errors
      console.error('Unexpected error while retrieving customer account:', error);
      return null;
    }
  };

  /**
   * Switch customer account with optional parameters like storeId and storeViewId.
   */
  const switchCustomerAccount = async (params: {
    customerAccountId?: string;
    storeId?: string;
    storeViewId?: string;
    currency?: string;
  }): Promise<GetAccountResponse | null> => {
    try {
      const { customerAccountId, storeId, storeViewId, currency } = params;
      console.log('switchCustomerAccount', params);
      console.log('customerAccountId', customerAccountId);
      // Set customer account ID in cookies (Ensure this function is correctly implemented)
      customerAccountId ? setAccountCookies(customerAccountId) : null;
      const accountcookie = getCookie('customer-account-id');
      console.log('accountcookie', accountcookie);
      storeId ? setStoreCookies(storeId) : null;
      storeViewId ? setStoreViewCookies(storeViewId) : null;
      currency ? setCurrencyCookies(currency) : null;

      // Use Nuxt's useFetch composable to make the request
      const { data, error } = await useFetch<GetAccountResponse>('/api/account/switch', {
        method: 'POST',
        body: {
          ...(customerAccountId ? { customerAccountId } : {}),
          ...(storeId ? { storeId } : {}),
          ...(storeViewId ? { storeViewId } : {}),
        },
      });

      // Handle errors
      if (error.value) {
        console.error('Failed to switch customer account:', error.value);
        return null;
      }
      console.log('data', data.value);
      // Update user store if the response contains account data
      if (data.value?.account) {
        console.log('update store account', data.value.account);
        const userStore = useUserStore(); // Ensure this store is imported properly
        userStore.account = data.value.account;
        storeId && storeViewId ? (userStore.store = { storeId, storeViewId }) : null;
        userStore.currency = currency;
      }

      // Return the updated account data
      return data.value ?? null;
    } catch (err) {
      console.error('Unexpected error while switching customer account:', err);
      return null;
    }
  };

  /**
   * Create a new customer account with optional configuration.
   */
  const createCustomerAccount = async (
    params: CreateCustomerAccountRequest,
    config: Partial<DjustConfig> = {}
  ): Promise<CreateAccountResponse | null> => {
    await ensureAuthenticated();

    try {
      const { data, error } = await useFetch<CreateAccountResponse>('/api/account/create', {
        method: 'POST',
        query: config,
        body: params,
      });

      if (error.value) {
        console.error('Failed to create customer account:', error.value);
        return null;
      }

      return data.value;
    } catch (error) {
      console.error('Unexpected error while creating customer account:', error);
      return null;
    }
  };

  /**
   * Update an existing customer account with optional configuration.
   */
  const updateCustomerAccount = async (
    params: UpdateCustomerAccountRequest,
    config: Partial<DjustConfig> = {}
  ): Promise<UpdateAccountResponse | null> => {
    await ensureAuthenticated();
    try {
      const { data, error } = await useFetch<UpdateAccountResponse>('/api/account/update', {
        method: 'PUT',
        body: params,
        query: config,
      });

      if (error.value) {
        console.error('Failed to update customer account:', error.value);
        return null;
      }

      return data.value;
    } catch (error) {
      console.error('Unexpected error while updating customer account:', error);
      return null;
    }
  };

  /**
   * Retrieve addresses for a customer account with optional configuration.
   */
  const getCustomerAccountAddresses = async (
    config: Partial<DjustConfig> = {}
  ): Promise<GetAccountAddressesResponse | null> => {
    await ensureAuthenticated();

    try {
      const { data, error } = await useFetch<GetAccountAddressesResponse>(
        '/api/account/addresses',
        {
          method: 'GET',
          query: config,
        }
      );

      if (error.value) {
        console.error('Failed to retrieve customer account addresses:', error.value);
        return null;
      }

      return data.value;
    } catch (error) {
      console.error('Unexpected error while retrieving customer account addresses:', error);
      return null;
    }
  };

  /**
   * Create an address for a customer account with optional configuration.
   */
  const createCustomerAccountAddress = async (
    params: CreateCustomerAccountAddressRequest,
    config: Partial<DjustConfig> = {}
  ): Promise<CreateAccountAddressResponse | null> => {
    await ensureAuthenticated();

    try {
      const { data, error } = await useFetch<CreateAccountAddressResponse>(
        '/api/account/address/create',
        {
          method: 'POST',
          body: params,
          query: config,
        }
      );

      if (error.value) {
        console.error('Failed to create customer account address:', error.value);
        return data.value;
      }

      return data.value;
    } catch (error) {
      console.error('Unexpected error while creating customer account address:', error);
      return null;
    }
  };

  /**
   * Update an address for a customer account with optional configuration.
   */
  const updateCustomerAccountAddress = async (
    params: UpdateCustomerAccountAddressRequest,
    config: Partial<DjustConfig> = {}
  ): Promise<UpdateAccountAddressResponse | null> => {
    await ensureAuthenticated();

    try {
      const { data, error } = await useFetch<UpdateAccountAddressResponse>(
        `/api/account/addresses/${params.addressId}/update`,
        {
          method: 'PUT',
          query: config,
          body: params,
        }
      );

      if (error.value) {
        console.error('Failed to update customer account address:', error.value);
        return null;
      }

      return data.value;
    } catch (error) {
      console.error('Unexpected error while updating customer account address:', error);
      return null;
    }
  };

  /**
   * Delete an address from a customer account with optional configuration.
   */
  const deleteCustomerAccountAddress = async (
    addressId: string,
    config: Partial<DjustConfig> = {}
  ): Promise<DeleteAccountAddressResponse | null> => {
    await ensureAuthenticated();

    try {
      const { data, error } = await useFetch<DeleteAccountAddressResponse>(
        `/api/account/addresses/${addressId}/delete`,
        {
          method: 'DELETE',
          query: config,
        }
      );

      if (error.value) {
        console.error('Failed to delete customer account address:', error.value);
        return data.value;
      }

      return data.value;
    } catch (error) {
      console.error('Unexpected error while deleting customer account address:', error);
      return null;
    }
  };

  /**
   * Retrieve users of a customer account with optional configuration.
   */
  const getCustomerAccountUsers = async (
    params: GetCustomerAccountUsersRequest,
    config: Partial<DjustConfig> = {}
  ): Promise<GetAccountUsersResponse | null> => {
    await ensureAuthenticated();

    try {
      const { data, error } = await useFetch<GetAccountUsersResponse>('/api/account/users', {
        method: 'GET',
        query: { ...config, ...params }, // Pass optional parameters as query params
      });

      if (error.value) {
        console.error('Failed to retrieve customer account users:', error.value);
        return data.value;
      }

      return data.value;
    } catch (error) {
      console.error('Unexpected error while retrieving customer account users:', error);
      return { success: false, users: [] };
    }
  };
  return {
    getCustomerAccount,
    createCustomerAccount,
    updateCustomerAccount,
    getCustomerAccountAddresses,
    createCustomerAccountAddress,
    updateCustomerAccountAddress,
    deleteCustomerAccountAddress,
    getCustomerAccountUsers,
    switchCustomerAccount,
  };
}
